import {
  Box,
  Typography,
  Button,
  Grid,
} from "@mui/material";
import { makeStyles, useTheme } from "@mui/styles";
import { getLocalizedText } from "../../logic/Utils";
import SectionLayout from "../../layout/SectionLayout";
import { SectionTitle, SectionSubtitle } from "../../../Template3/components/SectionTitle";
import useServiceCard from "../../logic/useServiceCard";
import { textVariant, textClasses } from "../../config";

// --------------------------------------
//  Styles
// --------------------------------------
const useStyles = makeStyles((theme) => ({
  rootContainer: {
    width: "100%",
    backgroundColor: "#FFFFFF", 
  },
  headingsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    marginBottom: theme.spacing(4),
  },
  viewAllWrapper: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  viewAllButton: {
    borderRadius: 10,
    border:'1px solid #F2F2F2',
    backgroundColor:'#FFFFFF',
    padding: `${theme.spacing(1.5)} ${theme.spacing(4)}`,
    color: "#0F2240",
    fontSize: "18px",
    textTransform: "capitalize",
    fontWeight: 500,
    transition: "background-color 0.3s ease",
    cursor: "pointer",
    width:'calc(229px - 48px)',
        height:63,
    "&:hover": {
      backgroundColor: '#0000001a',
    },
  },

  root:{
    justifyContent:'center'
  },

  cardWrapper: {
    padding: "10px 24px",
    borderRadius: 12,
    minWidth:'calc(216px - 48px)',
    backgroundColor: "#F4F4F4",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 'calc(60px - 20px)',
    transition: "all 0.3s ease",
    border: "1px solid transparent",
    cursor: 'pointer',
    "&:hover": {
        backgroundColor: "#fff !important",
        border: "1px solid #0F2240",
    },
  },

  cardTitle: {
    position: "relative",
    zIndex: 2,
    color: "#fff",
    fontWeight: 500,
  },
}));

// --------------------------------------
//  Services Component
// --------------------------------------
export function Services({
  services,
  categories,
  t,
  handleExploreServices,
  handleExploreCategories,
  productType,
  data: dataObj,
  mainColor,
  contentData,
  index,
}) {
    const classes = useStyles();
    const theme = useTheme();
    const { chatgptContent } = dataObj || {};
    const companyId = dataObj?.companyData?.companyId || "";

    const {
        maxDisplayItems,
        backgroundColor,
        backgroundImage,
        backgroundStyle,
        title,
        arTitle,
        subTitle,
        arSubTitle,
        titleFormat,
        titleFontSize,
        subTitleFontSize,
        titleFontColor,
        subTitleFontColor,
        paddingTop,
        paddingBottom,
    } = contentData || {};
    const maxServiceShow = maxDisplayItems ? maxDisplayItems : 9;

    const isRTL = dataObj?.isRTL === "ar";

    const _title = title
    ? getLocalizedText(title, arTitle)
    : productType === 'categories'
    ? t?.("companyPage.exploreAllCategories")
    : t?.("companyPage.exploreAllServices");
    
    const _subTitle = subTitle
    ? getLocalizedText(subTitle, arSubTitle)
    : t?.("companyPage.exploreTitle2");

    // 2) Filter logic
    const servicesCopy = services && services.length ? [...services] : [];
    const filterByCat =
        servicesCopy.length && contentData?.categoryId
        ? servicesCopy.filter(
            (srv) => srv.serviceCategoryId === contentData.categoryId
            )
        : servicesCopy;

    const selectedCats = contentData?.selectedCategories || [];
    const filteredCategories =
        selectedCats.length && categories
        ? categories.filter((cat) => selectedCats.includes(cat.id))
        : categories || [];

    const renderList =
        productType === "categories"
        ? filteredCategories.length
            ? filteredCategories
            : categories
        : filterByCat;

    if (!renderList || renderList.length === 0) {
        return null;
    }

  return (
    <Box
        className={classes.rootContainer}
        style={{
            ...(backgroundStyle === "image" && backgroundImage
            ? {
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: "cover",
                backgroundRepeat: "no-repeat",
                }
            : backgroundStyle === "gradient"
            ? { backgroundImage: backgroundColor }
            : { backgroundColor: backgroundColor || "#FFFFFF" }),
        }}
    >
        <SectionLayout paddingTop={paddingTop} paddingBottom={paddingBottom}>
            {/* Centered Headings */}
            <Box className={classes.headingsContainer}>
                <SectionSubtitle
                    visibleHorizontalLine={false}
                    pFontSize={subTitleFontSize}
                    color={subTitleFontColor}
                    mainColor={mainColor}
                >
                    {_title}
                </SectionSubtitle>
                <SectionTitle
                    pFontSize={titleFontSize}
                    color={titleFontColor}
                    variant={titleFormat}
                >
                    {_subTitle}
                </SectionTitle>
            </Box>

            <Grid container spacing={2} className={classes.root}>
                {renderList?.slice(0, maxServiceShow).map((item, idx) => {
                    // Determine localized name
                    const displayName =
                        chatgptContent && Object.keys(chatgptContent).length
                        ? isRTL
                            ? chatgptContent.arabic[`section${index}`][idx].title
                            : chatgptContent.english[`section${index}`][idx].title
                        : item.name;

                    // Pass down a “service” prop with name + image
                    const serviceWithName = {
                        ...item,
                        name: displayName
                    };

                    return (
                        <ServiceCard
                        key={idx}
                        service={serviceWithName}
                        productType={productType}
                        companyId={companyId}
                        contentData={contentData}
                        />
                    );
                })}
            </Grid>
            {/* “View All →” Button */}
            <Box className={classes.viewAllWrapper}>
            <Button
                disableElevation
                className={classes.viewAllButton}
                onClick={() =>
                productType === "categories"
                    ? handleExploreCategories()
                    : handleExploreServices()
                }
            >
                {t && t("services.viewAll")}
            </Button>
            </Box>
        </SectionLayout>
    </Box>
  );
}

function ServiceCard({ service, productType, companyId, contentData }) {
    const classes = useStyles();
    const { handleClick } = useServiceCard({ service, productType, companyId });
  
    const titleFontSize = contentData?.cardTitleFontSize ?? '21';
    const titleFontColor = contentData?.cardTitleColor ?? '';
    const cardRadius = contentData?.cardRadius ? `${contentData.cardRadius}px` : '10px';
    const cardBorder = contentData?.border || '1px solid #F2F2F2';
    return (
        <Grid
            item
            onClick={handleClick}
        >
            <Box
                className={classes.cardWrapper}
                border={cardBorder}
                borderRadius={cardRadius}
                style={{
                    cursor: 'pointer',
                    ...((contentData?.cardBackgroundStyle === 'image' && contentData?.cardBackgroundImage) ? 
                    {
                        backgroundSize: "cover",
                        backgroundImage: `url(${contentData?.cardBackgroundImage})`,
                        backgroundRepeat: "no-repeat",
                    } 
                    : 
                    (contentData?.cardBackgroundStyle === 'gradient' && contentData?.cardBackgroundColor) ? 
                    {
                        backgroundImage: contentData?.cardBackgroundColor ? contentData?.cardBackgroundColor : '#F1F0F0',
                    } 
                    :
                    (contentData?.cardBackgroundStyle === 'solidColor' && contentData?.cardBackgroundColor) ? 
                    {
                        backgroundColor: contentData?.cardBackgroundColor || '#F1F0F0',
                    } 
                    : 
                    {backgroundColor: '#F1F0F0'}
                    ),
                    overflow:'hidden',
            }}
            >
                <Typography
                variant={textVariant?.[titleFontSize]}
                className={`twoLineTxt ${classes.cardTitle} ${textClasses?.[titleFontSize]}`}
                align="center"
                style={{
                    color: titleFontColor || "#0F2240",
                    fontWeight: 600,
                }}
                >
                {service?.name}
                </Typography>
            </Box>
        </Grid>
    );
  }